import { saveAs } from "file-saver";
import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import "../css/DataTable.css";

// ================== HELPERS GLOBALES (FUERA DEL COMPONENTE) ==================

const extraerTexto = (val) => {
  if (val === null || val === undefined) return "";

  // string/number/bool
  if (typeof val === "string" || typeof val === "number" || typeof val === "boolean") {
    return String(val);
  }

  // Date
  if (val instanceof Date) return val.toISOString();

  // Array
  if (Array.isArray(val)) return val.map(extraerTexto).join(" ");

  // React element (JSX): usa props.children
  if (typeof val === "object" && val?.props) {
    return extraerTexto(val.props.children);
  }

  // Object normal
  try {
    return JSON.stringify(val);
  } catch {
    return "";
  }
};

const normalizar = (str) =>
  (str ?? "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // quita tildes
    .toLowerCase()
    .replace(/[^a-z0-9]+/gi, " ") // quita signos (. , / - etc) y deja espacios
    .trim()
    .replace(/\s+/g, " "); // colapsa espacios

const soloDigitos = (str) => (str ?? "").toString().replace(/\D+/g, "");

const CLAVES_EXCLUIDAS = new Set(["funciones", "imagen", "__v"]);

// ================== COMPONENT ==================

const DataTable = ({ columns, data }) => {
  const [globalFilter, setGlobalFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(0);

  // 🔹 NOVEDAD (sin cambiar estilos)
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = useMemo(() => [5, 10, 15, 50, 100], []);

  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleDownloadExcel = () => {
    const ws = XLSX.utils.json_to_sheet(data || []);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Datos");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, "data.xlsx");
  };

  const handleSort = (columnId) => {
    let direction = "asc";
    if (sortConfig.key === columnId && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key: columnId, direction });
  };

  // ✅ FILTRO GLOBAL: busca por TODOS los campos existentes (incluye JSX y números)
  const filteredData = useMemo(() => {
    const qRaw = globalFilter;
    const q = normalizar(qRaw);
    if (!q) return data || [];

    const qDigits = soloDigitos(qRaw);

    return (data || []).filter((row) => {
      if (!row || typeof row !== "object") return false;

      // keys del row + keys de columns
      const keysRow = Object.keys(row);
      const keysCols = (columns || []).map((c) => c?.accessor).filter(Boolean);

      const keys = Array.from(new Set([...keysRow, ...keysCols])).filter(
        (k) => !CLAVES_EXCLUIDAS.has(k)
      );

      const textoFila = keys.map((k) => extraerTexto(row[k])).join(" ");
      const textoNorm = normalizar(textoFila);

      // letras + coincidencias parciales
      if (textoNorm.includes(q)) return true;

      // números (ej: 12345 encuentra 12.345 / 12-345)
      if (qDigits) {
        const filaDigits = soloDigitos(textoFila);
        if (filaDigits.includes(qDigits)) return true;
      }

      return false;
    });
  }, [data, columns, globalFilter]);

  const sortedData = useMemo(() => {
    const arr = [...filteredData];
    if (!sortConfig.key) return arr;

    arr.sort((a, b) => {
      const aVal = a?.[sortConfig.key];
      const bVal = b?.[sortConfig.key];

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return arr;
  }, [filteredData, sortConfig]);

  const pageCount = Math.ceil(sortedData.length / rowsPerPage) || 1;

  useEffect(() => {
    if (currentPage > pageCount - 1) setCurrentPage(0);
  }, [pageCount, currentPage]);

  const paginatedData = useMemo(() => {
    const start = currentPage * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, currentPage, rowsPerPage]);

  const total = sortedData.length;
  const startRow = total === 0 ? 0 : currentPage * rowsPerPage + 1;
  const endRow = Math.min(startRow + rowsPerPage - 1, total);

  return (
    <div className="data-table-container">
      {/* 🔍 BUSCADOR + BOTÓN (SIN CAMBIOS) */}
      <div className="d-flex align-items-center mb-3">
        <input
          type="text"
          placeholder="Buscar..."
          value={globalFilter}
          onChange={(e) => {
            setGlobalFilter(e.target.value);
            setCurrentPage(0);
          }}
          className="form-control me-3"
        />
        <div className="col">
          <button
            onClick={handleDownloadExcel}
            className="btn btn-download"
            style={{ width: "30px", height: "30px", padding: "0" }}
            title="Descargar Excel"
            type="button"
          >
            <i className="fa-solid fa-download"></i>
          </button>
        </div>
      </div>

      {isMobile ? (
        paginatedData.map((row, rowIndex) => (
          <div key={rowIndex} className="card mb-5 shadow-sm" style={{ maxWidth: "540px" }}>
            <div className="card-body">
              <div className="row">
                {columns.map((column) => (
                  <div key={column.accessor} className="col-12 col-md-6 mb-3">
                    {column.accessor !== "funciones" ? (
                      <div className="d-flex flex-column border-bottom pb-2">

                        {!column.hideHeaderOnMobile && (
                          <span className="text-muted small text-start">
                            {column.Header}
                          </span>
                        )}

                        <span className="fw-semibold text-end text-break mt-1">
                          {row?.[column.accessor]}
                        </span>

                      </div>
                    ) : (
                      row?.[column.accessor]
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))
      ) : (

        // 🖥️ TABLA (SIN CAMBIOS)
        <div className="table-responsive">
          <table className="data-table table table-hover">
            <thead>
              <tr>
                {columns
                  .filter((c) => c.accessor !== "email")
                  .map((column) => (
                    <th key={column.accessor}>
                      <div className="d-flex align-items-center">
                        <span className="me-2">{column.Header}</span>
                        {column.accessor !== "funciones" && column.accessor !== "imagen" && (
                          <button
                            onClick={() => handleSort(column.accessor)}
                            className="btn btn-sm btn-link p-0"
                            type="button"
                          >
                            {sortConfig.key === column.accessor
                              ? sortConfig.direction === "asc"
                                ? "🔼"
                                : "🔽"
                              : "↕"}
                          </button>
                        )}
                      </div>
                    </th>
                  ))}
              </tr>
            </thead>

            <tbody>
              {paginatedData.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {columns
                    .filter((c) => c.accessor !== "email")
                    .map((column) => (
                      <td key={column.accessor}>{row?.[column.accessor]}</td>
                    ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ✅ BARRA INFERIOR (MISMO ESTILO) */}
      <div className="mt-3 border-top pt-3">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center gap-3">

          {/* IZQUIERDA (desktop) / ARRIBA (móvil) */}
          <div className="text-center text-md-start">
            <span className="small text-secondary">
              Mostrando <strong>{startRow}</strong> – <strong>{endRow}</strong> de{" "}
              <strong>{total}</strong>
            </span>
          </div>

          {/* DERECHA: CONTROLES */}
          <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-md-end gap-2">

            {/* Filas */}
            <div className="d-flex align-items-center justify-content-between justify-content-md-start gap-2">
              <span className="small text-secondary">Filas:</span>

              <select
                className="form-select-sm w-100"
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrentPage(0);
                }}
                style={{ maxWidth: 1200 }}  // Solo limita en pantallas grandes
              >
                {rowsPerPageOptions.map((op) => (
                  <option key={op} value={op}>
                    {op}
                  </option>
                ))}
              </select>

            </div>

            {/* Botones + página */}
            <div className="d-flex align-items-center justify-content-between justify-content-md-end gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                disabled={currentPage === 0}
                className="btn btn-outline-secondary btn-sm"
                type="button"
              >
                <i className="fa-solid fa-chevron-left me-2"></i>
                Anterior
              </button>

              <span className="small text-secondary px-1">
                <strong>{currentPage + 1}</strong> / <strong>{pageCount}</strong>
              </span>

              <button
                onClick={() => setCurrentPage((p) => Math.min(pageCount - 1, p + 1))}
                disabled={currentPage >= pageCount - 1}
                className="btn btn-outline-secondary btn-sm"
                type="button"
              >
                Siguiente
                <i className="fa-solid fa-chevron-right ms-2"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default DataTable;
